import React, { useMemo, useRef } from 'react';

import type { EChartsOption } from 'echarts';
import { useLocation, useNavigate } from 'react-router-dom';
import { lensTokens } from '~/configs/unifiedAnalyticsTheme';
import { type NativePrintProps, NativeReportShell } from '~/nativeLenses/NativePrintBoard';
import { nativeLensId } from '~/nativeLenses/nativeLensId';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Container,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import PageChatButton from '~/pages/AskRichardUnified/page/PageChatButton';
import { usePageBridge } from '~/pages/AskRichardUnified/page/pageBridge';

import { useGetReportingLeLensData } from '~/hooks/apis/reporting/useGetReportingLeLensData';

import type ApiError from '~/types/ApiError';

import ProductPerformanceTrend from '../ProductPerformance/ProductPerformanceTrend';
import { type CardProblem, type CardResult, isProblem, periodLabel, readCard } from '../ProductPerformance/model';

import LoyaltySocieteTable from './LoyaltySocieteTable';
import { barOption, tierBarOption, trendOption } from './charts';
import { type Column, type ValueFormat, formatValue, sumColumn } from './format';
import {
  BURN_METRICS,
  EARN_METRICS,
  FILTER_DEFINITIONS,
  GRANULARITIES,
  type Granularity,
  LOYALTY_SOCIETE_PAGE_ID,
  LOYALTY_VIEWS,
  type LoyaltyFilters,
  type LoyaltyView,
  POINT_VALUE_AUD,
  PURCHASE_METRICS,
  PURCHASE_SPLITS,
  bucketPoints,
  byTier,
  defaultFilters,
  filtersError,
  firstRow,
  groupRows,
  lensFilters,
  migrationBreakdown,
  monthStart,
  parseFilters,
  pivotByTier,
  ratio,
  readMigrations,
  sumMigrations,
  writeFilters,
} from './model';
import { editFromBridge, loyaltySocietePageView } from './pageAdapter';

const LENS_ID = nativeLensId(LOYALTY_SOCIETE_PAGE_ID);
const SNAPSHOT_LABEL = 'Current balances';
const MEMBER_MEASURES = [
  'members',
  'active_members',
  'members_excl_shadow_banned',
  'points_outstanding',
  'points_pending',
  'points_redeemed',
  'status_credits',
  'nights',
];
const ACTIVITY_MEASURES = [
  'points_earned',
  'points_burned',
  'status_credits_accrued',
  'lounges_redeemed',
  'upgrades_redeemed',
];
const PURCHASE_MEASURES = ['items', 'base_points', 'bonus_points', 'total_points', 'ttv_aud'];
const PARTNER_MEASURES = ['base_points', 'transactions', 'societe_accounts', 'customers'];
const partnerColumns: Array<Column> = [
  sumColumn('base_points', 'Base points'),
  sumColumn('transactions', 'Transactions'),
  sumColumn('societe_accounts', 'Société accounts'),
  sumColumn('customers', 'Customers'),
];
const EARN_BURN_MEASURES = [
  'points_issued',
  'points_pending',
  'points_earned',
  'points_earned_base',
  'points_earned_bonus',
  'points_burned',
  'points_burned_base',
  'points_burned_bonus',
  'ttv_earned_aud',
  'burned_value_aud',
  'points_burned_valued',
  'items',
];
const earnedColumns: Array<Column> = [
  sumColumn('points_earned_base', 'Base points'),
  sumColumn('points_earned_bonus', 'Bonus points'),
  sumColumn('points_earned', 'Total points'),
  sumColumn('ttv_earned_aud', 'TTV earned', 'aud'),
  sumColumn('items', 'Item count'),
  {
    key: 'points_per_dollar',
    label: 'Points / $',
    format: 'decimal',
    value: (v) => ratio(v.points_earned, v.ttv_earned_aud),
  },
];
const burnedColumns: Array<Column> = [
  sumColumn('points_burned_base', 'Base points'),
  sumColumn('points_burned_bonus', 'Bonus points'),
  sumColumn('points_burned', 'Total points'),
  sumColumn('burned_value_aud', 'Value of points burned', 'aud'),
  {
    key: 'value_per_point',
    label: 'Value / point',
    format: 'decimal',
    value: (v) => ratio(v.burned_value_aud, v.points_burned_valued),
  },
];
const BENEFITS = [
  { key: 'airport_transfers', label: 'Airport transfers' },
  { key: 'upgrades', label: 'Upgrades' },
  { key: 'lounge_passes', label: 'Lounge passes' },
  { key: 'travelshoot_vouchers', label: 'Travelshoot vouchers' },
  { key: 'intl_esim', label: 'Intl Travel eSIM' },
];
const purchaseColumns: Array<Column> = [
  sumColumn('items', 'Item count'),
  sumColumn('base_points', 'Base points'),
  sumColumn('bonus_points', 'Bonus points'),
  sumColumn('total_points', 'Total points'),
  sumColumn('ttv_aud', 'TTV AUD', 'aud'),
  { key: 'dollar_per_point', label: '$ per point', format: 'decimal', value: (v) => ratio(v.ttv_aud, v.total_points) },
];
const tierColumns: Array<Column> = [
  sumColumn('members', 'Members'),
  sumColumn('active_members', 'Active members'),
  { key: 'active_share', label: 'Active %', format: 'percent', value: (v) => ratio(v.active_members, v.members) },
  sumColumn('points_outstanding', 'Points outstanding'),
  {
    key: 'avg_points',
    label: 'Avg points / member',
    format: 'decimal',
    value: (v) => ratio(v.points_outstanding, v.members),
  },
  {
    key: 'avg_pending',
    label: 'Avg pending points / member',
    format: 'decimal',
    value: (v) => ratio(v.points_pending, v.members),
  },
  { key: 'avg_sc', label: 'Avg SC / member', format: 'decimal', value: (v) => ratio(v.status_credits, v.members) },
  sumColumn('nights', 'Nights'),
];
const regionColumns: Array<Column> = [
  sumColumn('members', 'Members'),
  sumColumn('active_members', 'Active members'),
  sumColumn('points_outstanding', 'Points outstanding'),
  {
    key: 'points_outstanding_aud',
    label: 'Points outstanding ($)',
    format: 'aud',
    value: (v) => (v.points_outstanding === null ? null : v.points_outstanding * POINT_VALUE_AUD),
  },
  sumColumn('points_pending', 'Points pending'),
  sumColumn('points_redeemed', 'Points redeemed'),
  sumColumn('status_credits', 'Status credits'),
];

type Kpi = { label: string; value: number | null; format?: ValueFormat; note?: string };

function LoyaltySocieteContent() {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const location = useLocation();
  const navigate = useNavigate();
  const filters = useMemo(() => parseFilters(new URLSearchParams(location.search)), [location.search]);
  const { purchaseMetric, earnMetric, burnMetric } = filters;
  const update = (change: Partial<LoyaltyFilters>, resetDates = false, replace = false) => {
    const next = { ...filters, ...change };
    const current = new URLSearchParams(location.search);
    for (const key of ['from', 'to'] as const) {
      if (resetDates) current.delete(key);
      else if (typeof change[key] === 'string') current.set(key, change[key]);
    }
    navigate({
      pathname: location.pathname,
      search: writeFilters(next, current).toString(),
      hash: location.hash,
    }, { replace });
  };
  const error = filtersError(filters);
  // Filter values come back with each load; the last full list stays in place while the next one loads.
  const lastOptions = useRef<Record<string, Array<string>>>({});
  const request = useMemo(() => lensFilters(filters), [filters]);
  const query = useGetReportingLeLensData(error ? undefined : LENS_ID, request);
  const cards = query.data?.cards ?? [];
  if (query.data?.filterOptions && Object.keys(query.data.filterOptions).length) {
    lastOptions.current = query.data.filterOptions;
  }
  const options = lastOptions.current;
  const ready = !error && query.isSuccess && !query.isFetching && !query.isShowingPreviousData;
  const failure = query.error as ApiError | null;
  const card = (id: string) => readCard(cards, id);
  const range = periodLabel(
    cards.find((item) => item.cardId === 'o_earn_burn'),
    `${filters.from} to ${filters.to}`,
  );
  const yesterday = periodLabel(
    cards.find((item) => item.cardId === 'o_activity_yesterday'),
    'Yesterday',
  );
  const rows = (id: string) => {
    const result = card(id);
    return isProblem(result) ? [] : result.rows;
  };

  const snapshot = firstRow(rows('o_snapshot'), MEMBER_MEASURES);
  const benefitKeys = BENEFITS.flatMap(({ key }) =>
    ['outstanding', 'redeemed', 'expired'].map((state) => `${key}_${state}`),
  );
  const benefits = {
    ...firstRow(rows('o_benefits'), benefitKeys),
    ...firstRow(
      rows('o_esim'),
      benefitKeys.filter((key) => key.startsWith('intl_esim_')),
    ),
  };
  const signups = firstRow(rows('o_signups'), ['members']);
  const activity = firstRow(rows('o_activity_yesterday'), ACTIVITY_MEASURES);
  const pendingYesterday = firstRow(rows('o_pending_yesterday'), ['points_pending']);
  const purchases = firstRow(rows('pp_summary'), PURCHASE_MEASURES);
  const earnBurn = firstRow(rows('eb_summary'), EARN_BURN_MEASURES);
  const categories = groupRows(rows('eb_category'), 'category', EARN_BURN_MEASURES);
  const migrations = readMigrations(rows('tm_monthly'));
  const partnerPoints = firstRow(rows('pc_points'), ['base_points', 'transactions']);
  const validations = firstRow(rows('pc_validations'), ['attempts', 'failed_attempts']);
  const successful = firstRow(rows('pc_successful'), ['successful_connections']);
  const connections = firstRow(rows('pc_connections'), ['connected_accounts', 'converted_accounts']);
  const partnerDistinct = groupRows(rows('pc_points_partner_distinct'), 'partner', ['societe_accounts', 'customers']);
  const partners = groupRows(rows('pc_points_partner'), 'partner', ['base_points', 'transactions']).map((row) => ({
    label: row.label,
    values: {
      ...row.values,
      ...(partnerDistinct.find((item) => item.label === row.label)?.values ?? {
        societe_accounts: null,
        customers: null,
      }),
    },
  }));
  const partnerSummary: Record<string, number | null> = {
    ...partnerPoints,
    ...validations,
    ...successful,
    ...connections,
    validation_failure_rate: ratio(validations.failed_attempts, validations.attempts),
    conversion_rate: ratio(connections.converted_accounts, connections.connected_accounts),
  };
  const lastMonth = monthStart(new Date(), -1);
  const thisMonth = monthStart(new Date());
  const actualBreakdown = migrationBreakdown(migrations, 'Actual');
  const forecastBreakdown = migrationBreakdown(migrations, 'Forecast');
  const tierSummary = {
    members_last_month: sumMigrations(
      migrations,
      (m) => m.dataType === 'Actual' && m.kind === 'Count' && m.month === lastMonth,
    ),
    new_bronze_last_month: sumMigrations(
      migrations,
      (m) => m.dataType === 'Actual' && m.kind === 'New' && m.tier === 'Bronze' && m.month === lastMonth,
    ),
    upgrades_last_month: sumMigrations(
      migrations,
      (m) => m.dataType === 'Actual' && m.kind === 'Up' && m.month === lastMonth,
    ),
    downgrades_last_month: sumMigrations(
      migrations,
      (m) => m.dataType === 'Actual' && m.kind === 'Down' && m.month === lastMonth,
    ),
    net_last_month: sumMigrations(migrations, (m) => m.dataType === 'Actual' && m.month === lastMonth, 'net'),
    net_this_month_forecast: sumMigrations(
      migrations,
      (m) => m.dataType === 'Forecast' && m.month === thisMonth,
      'net',
    ),
  };
  const tiers = groupRows(rows('m_tier_table'), 'tier_name', MEMBER_MEASURES).sort(byTier);
  const tierKpis = groupRows(rows('m_tier_kpis'), 'tier_name', ['members', 'active_members']);
  const regions = groupRows(rows('o_region'), 'loyalty_region', MEMBER_MEASURES).sort(
    (a, b) => (b.values.members ?? 0) - (a.values.members ?? 0),
  );
  const sumOf = (list: ReturnType<typeof groupRows>, key: string) =>
    list.length ? list.reduce((total, row) => total + (row.values[key] ?? 0), 0) : null;
  const regionTotal = {
    label: 'Grand Total',
    values: Object.fromEntries(MEMBER_MEASURES.map((key) => [key, sumOf(regions, key)])),
  };
  const tierValue = (tier: string) => tierKpis.find((row) => row.label === tier)?.values.members ?? null;
  const add = (...values: Array<number | null>) =>
    values.some((value) => value === null) ? null : values.reduce((total, value) => total + value, 0);

  const sections = (() => {
    if (filters.view === 'members') {
      return [{ title: 'Tier summary', rows: tiers }];
    }
    if (filters.view === 'points-earn-burn') {
      return [{ title: 'Points earned and burned by category', rows: categories }];
    }
    if (filters.view === 'partner-connection') {
      return [{ title: 'Points by partner', rows: partners }];
    }
    if (filters.view === 'tier-migration') {
      return [
        { title: 'Tier migration breakdown (actual)', rows: actualBreakdown.rows },
        { title: 'Tier forecast breakdown', rows: forecastBreakdown.rows },
      ];
    }
    if (filters.view === 'points-purchased') {
      return [
        {
          title: 'Pay for points by bracket',
          rows: groupRows(rows('pp_bracket'), 'points_bracket', PURCHASE_MEASURES),
        },
        {
          title: 'Pay for points by currency',
          rows: groupRows(rows('pp_currency'), 'currency_code', PURCHASE_MEASURES),
        },
      ];
    }
    return [
      { title: 'Loyalty by region', rows: regions },
      { title: 'Members and points by tier', rows: groupRows(rows('o_tier'), 'tier_name', MEMBER_MEASURES) },
    ];
  })();
  const summary =
    filters.view === 'points-purchased'
      ? purchases
      : filters.view === 'points-earn-burn'
        ? earnBurn
        : filters.view === 'tier-migration'
          ? tierSummary
          : filters.view === 'partner-connection'
            ? partnerSummary
            : filters.view === 'members'
              ? { ...snapshot, silver_members: tierValue('Silver'), gold_members: tierValue('Gold') }
              : { ...snapshot, ...activity, signups_yesterday: signups.members };
  const pageView = loyaltySocietePageView({ ready, filters, options, period: range, summary, sections });
  usePageBridge({
    pageId: LOYALTY_SOCIETE_PAGE_ID,
    view: pageView,
    edit: (changes) => {
      if (!ready) {
        throw new Error('Page data is still loading.');
      }
      update(editFromBridge(changes, pageView, filters, options), false, true);
    },
  });

  const problem = (result: CardResult | CardProblem) =>
    isProblem(result) ? (
      <Alert severity={query.isFetching ? 'info' : 'error'}>{query.isFetching ? 'Loading…' : result.problem}</Alert>
    ) : null;
  const kpiGrid = (title: string, caption: string, kpis: Array<Kpi>, problemOf?: CardResult | CardProblem) => (
    <Stack gap={1}>
      <Typography variant="subtitle2" color="text.secondary">
        {title} · {caption}
      </Typography>
      {problemOf && problem(problemOf)}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            md: `repeat(${Math.min(kpis.length, 6)}, minmax(0, 1fr))`,
          },
          gap: 1.5,
        }}
      >
        {kpis.map((kpi) => (
          <Paper key={kpi.label} variant="outlined" sx={{ p: 1.5, minWidth: 0, bgcolor: tokens.surface }}>
            <Typography variant="caption" color="text.secondary">
              {kpi.label}
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontSize: { xs: 20, sm: 22 }, fontVariantNumeric: 'tabular-nums', color: tokens.accent }}
            >
              {formatValue(kpi.value, kpi.format ?? 'number')}
            </Typography>
            {kpi.note && (
              <Typography variant="caption" color="text.secondary">
                {kpi.note}
              </Typography>
            )}
          </Paper>
        ))}
      </Box>
    </Stack>
  );
  const chart = (
    key: string,
    title: string,
    result: CardResult | CardProblem,
    option: () => EChartsOption,
    caption = range,
  ) =>
    isProblem(result) ? (
      <Box key={key}>{problem(result)}</Box>
    ) : (
      <Box key={key} sx={{ minWidth: 0, '& .MuiTypography-caption': { display: 'none' } }}>
        <ProductPerformanceTrend
          title={title}
          option={option()}
          source="lux-analytics-prod"
          range={caption}
          height={320}
        />
      </Box>
    );
  const table = (
    id: string,
    title: string,
    dimensionLabel: string,
    tableRows: ReturnType<typeof groupRows>,
    columns: Array<Column>,
    caption: string,
  ) => {
    const result = card(id);
    return isProblem(result) ? (
      <Box key={`${id}-${title}`}>{problem(result)}</Box>
    ) : (
      <LoyaltySocieteTable
        key={`${id}-${title}`}
        title={title}
        dimensionLabel={dimensionLabel}
        rows={tableRows}
        columns={columns}
        range={caption}
        fileName={`loyalty-societe-${id}-${filters.from}-${filters.to}.csv`}
      />
    );
  };
  const twoUp = { display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 2 };
  const threeUp = { display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, minmax(0, 1fr))' }, gap: 2 };
  const distribution = (id: string, title: string, band: string) => {
    const result = card(id);
    return chart(
      id,
      title,
      result,
      () => {
        const pivot = pivotByTier(rows(id), band, 'members');
        return barOption(
          pivot.bands,
          pivot.tiers.map((tier) => ({ name: tier, data: pivot.bands.map((label) => pivot.value(label, tier)) })),
          true,
        );
      },
      SNAPSHOT_LABEL,
    );
  };

  const overview = (
    <Stack gap={2}>
      {kpiGrid(
        'Société',
        SNAPSHOT_LABEL,
        [
          { label: 'Société members', value: snapshot.members },
          {
            label: 'Points outstanding',
            value: snapshot.points_outstanding,
            note:
              snapshot.points_outstanding === null
                ? undefined
                : formatValue(snapshot.points_outstanding * POINT_VALUE_AUD, 'aud'),
          },
          {
            label: 'Points pending',
            value: snapshot.points_pending,
            note:
              snapshot.points_pending === null
                ? undefined
                : formatValue(snapshot.points_pending * POINT_VALUE_AUD, 'aud'),
          },
          { label: 'Points redeemed', value: snapshot.points_redeemed },
          { label: 'Status credits outstanding', value: snapshot.status_credits },
          {
            label: 'Lounges / upgrades redeemed',
            value: add(benefits.lounge_passes_redeemed, benefits.upgrades_redeemed),
          },
        ],
        card('o_snapshot'),
      )}
      {kpiGrid(
        'Yesterday',
        yesterday,
        [
          { label: 'Sign-ups', value: signups.members },
          { label: 'Points earned', value: activity.points_earned },
          { label: 'Points pending', value: pendingYesterday.points_pending },
          { label: 'Points burned', value: activity.points_burned },
          { label: 'Status credits accrued', value: activity.status_credits_accrued },
          { label: 'Lounges / upgrades redeemed', value: add(activity.lounges_redeemed, activity.upgrades_redeemed) },
        ],
        card('o_activity_yesterday'),
      )}
      <Box sx={twoUp}>
        {chart('earn-burn', `Points earned and burned (${filters.granularity})`, card('o_earn_burn'), () =>
          trendOption([
            ...bucketPoints(rows('o_earn_burn'), 'activity_date_syd', 'points_earned', filters.granularity).map(
              (p) => ({
                ...p,
                series: 'Points earned',
              }),
            ),
            ...bucketPoints(rows('o_earn_burn'), 'activity_date_syd', 'points_burned', filters.granularity).map(
              (p) => ({
                ...p,
                series: 'Points burned',
              }),
            ),
          ]),
        )}
        {table(
          'o_benefits',
          'Benefits',
          'Benefit',
          BENEFITS.map(({ key, label }) => ({
            label,
            values: {
              outstanding: benefits[`${key}_outstanding`],
              redeemed: benefits[`${key}_redeemed`],
              expired: benefits[`${key}_expired`],
            },
          })),
          [sumColumn('outstanding', 'Outstanding'), sumColumn('redeemed', 'Redeemed'), sumColumn('expired', 'Expired')],
          SNAPSHOT_LABEL,
        )}
      </Box>
      <Box sx={threeUp}>
        {(
          [
            ['members', 'Members by tier'],
            ['points_outstanding', 'Outstanding points by tier'],
            ['points_redeemed', 'Points redeemed by tier'],
          ] as const
        ).map(([measure, title]) =>
          chart(
            `tier-${measure}`,
            title,
            card('o_tier'),
            () => {
              const grouped = groupRows(rows('o_tier'), 'tier_name', [measure]).sort(byTier);
              return tierBarOption(
                title,
                grouped.map((row) => row.label),
                grouped.map((row) => row.values[measure]),
              );
            },
            SNAPSHOT_LABEL,
          ),
        )}
      </Box>
      {table(
        'o_region',
        'Loyalty by region',
        'Loyalty region',
        [regionTotal, ...regions],
        regionColumns,
        SNAPSHOT_LABEL,
      )}
    </Stack>
  );

  const members = (
    <Stack gap={2}>
      {kpiGrid(
        'Members',
        `${SNAPSHOT_LABEL} · excludes shadow-banned members with no customer record`,
        [
          { label: 'Total members', value: sumOf(tierKpis, 'members') },
          { label: 'Silver members', value: tierValue('Silver') },
          { label: 'Gold members', value: tierValue('Gold') },
          { label: 'Active members', value: snapshot.active_members, note: 'All members' },
        ],
        card('m_tier_kpis'),
      )}
      <Box sx={twoUp}>
        {chart('enrolment', `Members by enrolment date by tier (${filters.granularity})`, card('m_enrolment'), () =>
          trendOption(
            bucketPoints(rows('m_enrolment'), 'enrolment_date_syd', 'members', filters.granularity, 'tier_name'),
            'bar',
          ),
        )}
        <Alert severity="info">
          Tier count over time is not available yet: its Tableau source (Loyalty_Societe_TierOverTime) has no
          lux-analytics-prod equivalent.
        </Alert>
      </Box>
      <Box sx={threeUp}>
        {distribution('m_sc_distribution', 'Distribution of status credits to next tier', 'sc_to_next_tier')}
        {distribution('m_nights_distribution', 'Distribution of nights to next tier', 'nights_to_next_tier')}
        {distribution('m_points_distribution', 'Distribution of points balance', 'points_balance_band')}
      </Box>
      {table('m_tier_table', 'Tier summary', 'Tier', tiers, tierColumns, SNAPSHOT_LABEL)}
    </Stack>
  );

  const monthLabel = (month: string) =>
    new Date(`${month}T00:00:00Z`).toLocaleDateString('en-AU', { month: 'short', year: '2-digit', timeZone: 'UTC' });
  const migrationPoints = (
    keep: (m: (typeof migrations)[number]) => boolean,
    series: (m: (typeof migrations)[number]) => string,
    measure: 'members' | 'net' = 'members',
  ) => migrations.filter(keep).map((m) => ({ date: m.month, series: series(m), value: m[measure] }));
  const tierMigration = (
    <Stack gap={2}>
      {kpiGrid(
        'Tier movement',
        `Last month is ${monthLabel(lastMonth)} · all customer types`,
        [
          { label: 'Members at month end', value: tierSummary.members_last_month },
          { label: 'New Bronze', value: tierSummary.new_bronze_last_month },
          { label: 'Upgrades', value: tierSummary.upgrades_last_month },
          { label: 'Downgrades', value: tierSummary.downgrades_last_month },
          { label: 'Net tier movement', value: tierSummary.net_last_month },
          {
            label: 'Net this month',
            value: tierSummary.net_this_month_forecast,
            note: `Forecast for ${monthLabel(thisMonth)}`,
          },
        ],
        card('tm_monthly'),
      )}
      <Box sx={threeUp}>
        {chart(
          'tm-new',
          'New enrolments by tier',
          card('tm_monthly'),
          () =>
            trendOption(
              migrationPoints(
                (m) => m.kind === 'New',
                (m) => m.tier,
              ),
              'bar',
            ),
          'Monthly since April 2025',
        )}
        {chart(
          'tm-net',
          'Net tier movement, actual and forecast',
          card('tm_monthly'),
          () =>
            trendOption(
              migrationPoints(
                (m) => m.kind !== 'Count',
                (m) => m.dataType,
                'net',
              ),
              'bar',
            ),
          'Monthly, with 12 months of forecast',
        )}
        {chart(
          'tm-count',
          'Members by tier at month end',
          card('tm_monthly'),
          () =>
            trendOption(
              migrationPoints(
                (m) => m.kind === 'Count' && m.tier !== 'Bronze',
                (m) => `${m.tier} (${m.dataType.toLowerCase()})`,
              ),
              'bar',
            ),
          'Silver and above; Bronze is in the table below',
        )}
      </Box>
      {table(
        'tm_monthly',
        'Tier migration breakdown',
        'Migration',
        actualBreakdown.rows,
        actualBreakdown.months.map((month) => sumColumn(month, monthLabel(month))),
        'Actual members moving, by month',
      )}
      {table(
        'tm_monthly',
        'Tier forecast breakdown',
        'Migration',
        forecastBreakdown.rows,
        forecastBreakdown.months.map((month) => sumColumn(month, monthLabel(month))),
        'Forecast from pending travel and membership year resets',
      )}
      <Typography variant="caption" color="text.secondary">
        The customer type split (organic, pre-qualified, complimentary) is not available: its source is outside
        lux-analytics-prod. Tier counts here count each member once.
      </Typography>
    </Stack>
  );

  const daysOrder = (label: string) => (label === 'Unknown' ? Number.POSITIVE_INFINITY : Number(label));
  const partnerConnection = (
    <Stack gap={2}>
      {kpiGrid(
        'Partners',
        `${range} · undated inferred connections always count`,
        [
          { label: 'Points', value: partnerSummary.base_points },
          { label: 'Validation attempts', value: partnerSummary.attempts },
          { label: 'Validation failure rate', value: partnerSummary.validation_failure_rate, format: 'percent' },
          { label: 'Successful connections', value: partnerSummary.successful_connections },
          { label: 'Connected accounts', value: partnerSummary.connected_accounts },
          { label: 'Conversion rate', value: partnerSummary.conversion_rate, format: 'percent' },
        ],
        card('pc_validations'),
      )}
      <Box sx={twoUp}>
        {table('pc_points_partner', 'Points by partner', 'Partner', partners, partnerColumns, range)}
        {chart('pc-points', `Points by partner (${filters.granularity})`, card('pc_points_daily'), () =>
          trendOption(
            bucketPoints(rows('pc_points_daily'), 'processed_date', 'base_points', filters.granularity, 'partner'),
          ),
        )}
      </Box>
      <Box sx={twoUp}>
        {chart('pc-validation', `Validation trend (${filters.granularity})`, card('pc_validation_daily'), () =>
          trendOption(
            [
              ...bucketPoints(rows('pc_validation_daily'), 'validation_day', 'attempts', filters.granularity).map(
                (point) => ({ ...point, series: 'Attempts' }),
              ),
              ...bucketPoints(
                rows('pc_validation_daily'),
                'validation_day',
                'failed_attempts',
                filters.granularity,
              ).map((point) => ({ ...point, series: 'Failed' })),
            ],
            'bar',
          ),
        )}
        {chart('pc-failed', 'Failed reasons', card('pc_failed_reasons'), () => {
          const reasons = groupRows(rows('pc_failed_reasons'), 'failure_reason', ['failed_attempts']).sort(
            (a, b) => (b.values.failed_attempts ?? 0) - (a.values.failed_attempts ?? 0),
          );
          return barOption(
            reasons.map((row) => row.label),
            [{ name: 'Failed attempts', data: reasons.map((row) => row.values.failed_attempts) }],
          );
        })}
      </Box>
      <Box sx={twoUp}>
        {chart('pc-days', 'Days from connection to first transaction', card('pc_days_to_first'), () => {
          const days = groupRows(rows('pc_days_to_first'), 'days_to_first_transaction', ['converted_accounts']).sort(
            (a, b) => daysOrder(a.label) - daysOrder(b.label),
          );
          return barOption(
            days.map((row) => row.label),
            [{ name: 'Converted accounts', data: days.map((row) => row.values.converted_accounts) }],
          );
        })}
        {chart('pc-conversion', 'Conversion rate by partner', card('pc_conversion_partner'), () => {
          const byPartner = groupRows(rows('pc_conversion_partner'), 'partner', [
            'connected_accounts',
            'converted_accounts',
          ]);
          return barOption(
            byPartner.map((row) => row.label),
            [
              {
                name: 'Conversion rate',
                data: byPartner.map((row) => ratio(row.values.converted_accounts, row.values.connected_accounts)),
              },
            ],
          );
        })}
      </Box>
    </Stack>
  );

  const metricSelect = (
    label: string,
    value: string,
    choices: ReadonlyArray<{ key: string; label: string }>,
    onChange: (next: string) => void,
  ) => (
    <TextField
      select
      size="small"
      label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      sx={{ maxWidth: 220 }}
    >
      {choices.map((choice) => (
        <MenuItem key={choice.key} value={choice.key}>
          {choice.label}
        </MenuItem>
      ))}
    </TextField>
  );
  const byCategory = (id: string, measure: string) =>
    trendOption(bucketPoints(rows(id), 'transaction_date_syd', measure, filters.granularity, 'category'));
  const pointsEarnBurn = (
    <Stack gap={2}>
      {kpiGrid(
        'Points',
        `${range} · sales-linked, after refunds, by transaction date`,
        [
          { label: 'Total issued', value: earnBurn.points_issued },
          { label: 'Total pending', value: earnBurn.points_pending },
          { label: 'Total burnt', value: earnBurn.points_burned },
          {
            label: 'Points earned / $',
            value: ratio(earnBurn.points_earned, earnBurn.ttv_earned_aud),
            format: 'decimal',
          },
          {
            label: 'Burn value / point',
            value: ratio(earnBurn.burned_value_aud, earnBurn.points_burned_valued),
            format: 'decimal',
          },
        ],
        card('eb_summary'),
      )}
      <Box sx={twoUp}>
        <Stack gap={1}>
          {metricSelect('Earned metric', earnMetric, EARN_METRICS, (earnMetric) => update({ earnMetric }))}
          {chart('eb-earned', `Points earned by category (${filters.granularity})`, card('eb_earned_daily'), () =>
            byCategory('eb_earned_daily', earnMetric),
          )}
        </Stack>
        <Stack gap={1}>
          {metricSelect('Burned metric', burnMetric, BURN_METRICS, (burnMetric) => update({ burnMetric }))}
          {chart('eb-burned', `Points burned by category (${filters.granularity})`, card('eb_burned_daily'), () =>
            byCategory('eb_burned_daily', burnMetric),
          )}
        </Stack>
      </Box>
      <Box sx={twoUp}>
        {table(
          'eb_category',
          'Points earned by category',
          'Category',
          categories.filter((row) => row.values.points_earned),
          earnedColumns,
          range,
        )}
        {table(
          'eb_category',
          'Points burned by category',
          'Category',
          categories.filter((row) => row.values.points_burned),
          burnedColumns,
          range,
        )}
      </Box>
    </Stack>
  );

  const metricLabel = PURCHASE_METRICS.find((metric) => metric.key === purchaseMetric)?.label ?? purchaseMetric;
  const purchaseSplit = filters.purchaseSplit;
  const purchaseCard =
    purchaseSplit === 'currency_code'
      ? 'pp_daily_currency'
      : purchaseSplit === 'points_bracket'
        ? 'pp_daily_bracket'
        : 'pp_daily';
  const purchaseSeries = purchaseSplit === 'none' ? undefined : purchaseSplit;
  const purchasePoints = () => {
    const daily = rows(purchaseCard);
    if (purchaseMetric !== 'dollar_per_point') {
      return bucketPoints(daily, 'transaction_date_sydt', purchaseMetric, filters.granularity, purchaseSeries);
    }
    const points = bucketPoints(daily, 'transaction_date_sydt', 'total_points', filters.granularity, purchaseSeries);
    const dollars = bucketPoints(daily, 'transaction_date_sydt', 'ttv_aud', filters.granularity, purchaseSeries);
    const denominators = new Map(
      points.map((point) => [`${point.date}|${purchaseSeries ? point.series : ''}`, point.value]),
    );
    return dollars.flatMap((point) => {
      const value = ratio(point.value, denominators.get(`${point.date}|${purchaseSeries ? point.series : ''}`) ?? null);
      return value === null ? [] : [{ ...point, value }];
    });
  };
  const pointsPurchased = (
    <Stack gap={2}>
      {kpiGrid(
        'Pay for points',
        range,
        [
          { label: 'Base points', value: purchases.base_points },
          { label: 'Bonus points', value: purchases.bonus_points },
          { label: 'Total points', value: purchases.total_points },
          { label: 'TTV AUD', value: purchases.ttv_aud, format: 'aud' },
          { label: '$ per point', value: ratio(purchases.ttv_aud, purchases.total_points), format: 'decimal' },
        ],
        card('pp_summary'),
      )}
      {metricSelect('Metric', purchaseMetric, PURCHASE_METRICS, (purchaseMetric) => update({ purchaseMetric }))}
      {metricSelect('Split by', purchaseSplit, PURCHASE_SPLITS, (purchaseSplit) => update({ purchaseSplit }))}
      {chart('pp-daily', `Pay for points · ${metricLabel} · ${PURCHASE_SPLITS.find((item) => item.key === purchaseSplit)?.label} (${filters.granularity})`, card(purchaseCard), () =>
        trendOption(
          purchasePoints(),
          'bar',
          purchaseMetric === 'dollar_per_point',
        ),
      )}
      <Box sx={twoUp}>
        {table(
          'pp_bracket',
          'Pay for points by bracket',
          'Points bracket',
          groupRows(rows('pp_bracket'), 'points_bracket', PURCHASE_MEASURES).sort(
            (a, b) => Number(a.label.replace(/,/g, '')) - Number(b.label.replace(/,/g, '')),
          ),
          purchaseColumns,
          range,
        )}
        {table(
          'pp_currency',
          'Pay for points by currency',
          'Currency',
          groupRows(rows('pp_currency'), 'currency_code', PURCHASE_MEASURES),
          purchaseColumns,
          range,
        )}
      </Box>
    </Stack>
  );

  const visibleFilters = FILTER_DEFINITIONS.filter((definition) =>
    (definition.views as ReadonlyArray<LoyaltyView>).includes(filters.view),
  );

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 }, px: { xs: 1.5, md: 3 }, minWidth: 0 }}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          gap={1}
        >
          <Box>
            <Typography component="h1" variant="h4" sx={{ fontSize: { xs: 25, sm: 30 }, color: tokens.accent }}>
              Loyalty Société
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Société members, points and benefits · Sydney dates · points at A$0.005
            </Typography>
          </Box>
          <PageChatButton />
        </Stack>
        <Tabs
          value={filters.view}
          onChange={(_, view: LoyaltyView) => update({ ...defaultFilters(view), selections: filters.selections }, true)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="Loyalty Société views"
        >
          {LOYALTY_VIEWS.map((view) => (
            <Tab key={view.value} value={view.value} label={view.label} />
          ))}
        </Tabs>
        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center" data-lens-pdf-ignore="">
          <Chip size="small" variant="outlined" color="primary" label="Live" />
          <Typography variant="caption" color="text.secondary">
            lux-analytics-prod · rebuilt daily · {query.isFetching ? 'updating…' : range}
          </Typography>
        </Stack>
        <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="center">
          <TextField
            type="date"
            size="small"
            label="From"
            value={filters.from}
            onChange={(event) => update({ from: event.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="date"
            size="small"
            label="To"
            value={filters.to}
            onChange={(event) => update({ to: event.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            select
            size="small"
            label="Date granularity"
            value={filters.granularity}
            onChange={(event) => update({ granularity: event.target.value as Granularity })}
            sx={{ minWidth: 150 }}
          >
            {GRANULARITIES.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>
          {visibleFilters.map(({ key, label }) => (
            <Autocomplete
              key={key}
              multiple
              size="small"
              options={options[key] ?? []}
              value={filters.selections[key]}
              onChange={(_, value) => update({ selections: { ...filters.selections, [key]: value } })}
              renderInput={(params) => <TextField {...params} label={label} placeholder="All" />}
              sx={{ minWidth: 200 }}
            />
          ))}
          <Button size="small" onClick={() => update({ ...defaultFilters(filters.view) }, true)}>
            Reset
          </Button>
        </Stack>
        {error && <Alert severity="error">{error}</Alert>}
        {failure && (
          <Alert
            severity={failure.status === 404 || failure.status === 403 ? 'warning' : 'error'}
            action={
              <Button color="inherit" onClick={() => void query.refetch()}>
                Retry
              </Button>
            }
          >
            {failure.status === 404
              ? 'Loyalty Société is not registered in Library for this environment yet. A reporting admin registers it under Playground → Lens pages → Register native pages.'
              : failure.status === 403
                ? 'You do not have access to Loyalty Société. Ask a reporting admin to add you to its Library folder.'
                : failure.message || 'Loyalty Société could not load.'}
          </Alert>
        )}
        {!error && !failure && query.isPending && (
          <Stack gap={2}>
            <Skeleton variant="rounded" height={90} />
            <Skeleton variant="rounded" height={320} />
          </Stack>
        )}
        {!error && query.data && (
          <>
            {filters.view === 'overview' && overview}
            {filters.view === 'members' && members}
            {filters.view === 'tier-migration' && tierMigration}
            {filters.view === 'points-earn-burn' && pointsEarnBurn}
            {filters.view === 'points-purchased' && pointsPurchased}
            {filters.view === 'partner-connection' && partnerConnection}
            <Typography
              variant="caption"
              color="text.secondary"
              data-lens-pdf-ignore=""
              sx={{ overflowWrap: 'anywhere', pb: 2 }}
            >
              Member balances, tiers and benefits are today&apos;s snapshot and ignore the dates. Not yet migrated from
              Tableau: Engagement, Engagement R12M, Benefit Redemptions and the two cohort views.
            </Typography>
          </>
        )}
      </Stack>
    </Container>
  );
}

export default function LoyaltySocietePage({ printTarget }: NativePrintProps) {
  return (
    <NativeReportShell
      pageId="loyalty-societe"
      title="Loyalty Société"
      printTarget={printTarget}
    >
      <LoyaltySocieteContent />
    </NativeReportShell>
  );
}
